import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import cx from 'classnames';
import filter from 'lodash/filter';
import split from 'lodash/split';
import indexOf from 'lodash/indexOf';
import Button from '@components/@core/AnimatedButton';
import FABButton from '@components/FABButton';
import { manageRoutes, developRoutes, vendorRoutes, communityRoutes, superAdminRoutes } from '~/routes/sidebar';
import AppActions from '~/actions/app';
import NavItem from './NavItem';

const SideNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const community = communityRoutes();
    const userRole = useSelector((state) => state.app.primary_role_id);
    const departments = useSelector((state) => state.company.blogCategories);
    const inProfile = useSelector((state) => state.app.inProfile);
    const communityUser = useSelector((state) => state.app.user?.community_user);

    const [expanded, setExpanded] = useState(true);

    const items = split(location.pathname, '/');
    const section = items[1];
    const path = location?.pathname;
    const isProfile = inProfile === 'user' || inProfile === 'company' || inProfile === 'content';

    const classes = cx('side-nav expanded', expanded && 'hide-menu');
    const currentPath = path.split('/')[1];
    const showHamburger = currentPath === 'hcm' || currentPath === 'library' || currentPath === 'forums' || currentPath === 'community' || currentPath === 'vendor-ratings';
    let developRoute = window?.location?.origin == 'https://hcm.drivingsales.com' ? filter((item) => item.title !== 'Documents', developRoutes) : developRoutes;

    const handleToggle = () => {
        setExpanded(!expanded);
    };

    const show = useMemo(() => {
        const sections = ['hcm', 'library', 'vendor-ratings', 'community', 'forums'];
        const index = indexOf(sections, section);
        if (index > -1 || isProfile) return true;
        else return false;
    }, [section, isProfile]);

    const handleLogin = () => {
        const route = '/community/profile';
        dispatch(AppActions.modalRequest({ type: 'Authentication', data: { before: { route } } }));
    };

    return (
        show && (
            <div className="main-nav">
                <div className={showHamburger ? 'side-nav-icon' : 'd-none'}>
                    <Button className={cx('pr-4', expanded ? 'open' : 'close')} onClick={handleToggle}>
                        <img src={expanded ? '/images/icons/menu.svg' : '/images/icons/delete.svg'} />
                    </Button>
                    <span className={expanded ? 'd-none' : 'overlay'} />
                </div>
                <div className={classes}>
                    {(section === 'hcm' || isProfile) &&
                        manageRoutes.map(
                            (route, index) =>
                                route.perm >= userRole && (
                                    <NavItem
                                        key={`mng-${index}`}
                                        title={route.title}
                                        dataCy={route.title.replace(/[^A-Z0-9]+/gi, '')}
                                        icon="far fa-circle"
                                        active={path === route.path || split(path, '/')[2] === split(route.path, '/')[2]}
                                        parent={route.header}
                                        collapsed={false}
                                        onClick={() => {
                                            handleToggle();
                                            navigate(route.path);
                                        }}
                                    />
                                )
                        )}

                    {section === 'library' &&
                        developRoute.map(
                            (route, index) =>
                                route.perm >= userRole && (
                                    <NavItem
                                        key={`lib-${index}`}
                                        title={route.title}
                                        dataCy={route.title.replace(/[^A-Z0-9]+/gi, '')}
                                        icon="far fa-circle"
                                        active={path === route.path || split(path, '/')[2] === split(route.path, '/')[2]}
                                        parent={route.header}
                                        collapsed={false}
                                        onClick={() => {
                                            handleToggle();
                                            navigate(route.path);
                                        }}
                                    />
                                )
                        )}

                    {(section === 'community' || section === 'forums' ) && (
                        <>
                            {community.map(
                                (route, index) =>
                                    route.perm >= userRole && (
                                        <NavItem
                                            key={`cmu-${index}`}
                                            title={route.title}
                                            icon="far fa-circle"
                                            active={path === route.path || split(path, '/')[2] === split(route.path, '/')[2]}
                                            parent={route.header}
                                            collapsed={false}
                                            onClick={() => {
                                                if (route.title === 'Profile') {
                                                    if (communityUser) {
                                                        window.location = `/${communityUser.entity.slug}/about`;
                                                    } else {
                                                        handleLogin();
                                                    }
                                                } else {
                                                    navigate(route.path);
                                                }
                                            }}
                                        />
                                    )
                            )}
                            <NavItem title="Departments" icon="far fa-circle" parent collapsed={false} />
                            {departments.map((item) => (
                                <NavItem
                                    key={`comm${item.id}`}
                                    title={item.name}
                                    icon="far fa-circle"
                                    active={path === `/community/departments/${item.slug}`}
                                    parent={false}
                                    collapsed={false}
                                    onClick={() => navigate(`/community/departments/${item.slug}`)}
                                />
                            ))}
                        </>
                    )}

                    {(section === 'vendor-ratings') &&
                        vendorRoutes.map(
                            (route, index) =>
                                route.perm >= userRole && (
                                    <NavItem
                                        key={`vrt-${index}`}
                                        title={route.title}
                                        dataCy={route.title.replace(/[^A-Z0-9]+/gi, '')}
                                        icon="far fa-circle"
                                        active={path === route.path || split(path, '/')[2] === split(route.path, '/')[2]}
                                        parent={route.header}
                                        collapsed={false}
                                        onClick={() => navigate(route.path)}
                                    />
                                )
                        )}

                    {inProfile === 'superadmin' &&
                        superAdminRoutes.map(
                            (route, index) =>
                                route.perm >= userRole && (
                                    <NavItem
                                        key={`sar-${index}`}
                                        title={route.title}
                                        dataCy={route.title.replace(/[^A-Z0-9]+/gi, '')}
                                        icon="far fa-circle"
                                        active={path === route.path || split(path, '/')[2] === split(route.path, '/')[2]}
                                        parent={route.header}
                                        collapsed={false}
                                        onClick={() => navigate(route.path)}
                                    />
                                )
                        )}

                    {/* <FABButton /> */}
                </div>
            </div>
        )
    );
};

export default SideNav;
